// =====================================================
// BROKER REGISTRATION API SERVICE
// =====================================================

import { BrokerFormData } from '@/types/broker-registration.types';
import apiClient, { handleApiError } from '@/services/api/client';

export interface BrokerRegistrationResponse {
  success: boolean;
  message: string;
  profileId?: string;
  data?: {
    email: string;
    firmName: string;
  };
}

/**
 * Transform frontend form data to backend DTO format
 * Seules les propriétés avec des valeurs sont incluses (pas de undefined/null/chaîne vide pour les optionnels)
 */
function transformFormDataToDto(formData: BrokerFormData) {
  // Propriétés obligatoires - toujours incluses
  const brokerName = formData.firmName
    ? formData.firmName
    : `${formData.firstName} ${formData.lastName}`.trim();

  const dto: Record<string, unknown> = {
    // Step 1: Profile (obligatoires)
    brokerName,
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    phone: formData.phone.slice(0, 30),
    password: formData.password,
    address: {
      addressType: formData.addressType,
      streetAddress: formData.streetAddress,
      city: formData.city,
      stateProvince: formData.stateProvince,
      zipPostalCode: formData.zipPostalCode,
      country: formData.country,
    },
    motherTongue: formData.motherTongue,

    // Step 2: Expertise & Licenses (obligatoires)
    specializations: formData.specializations,
    licenses: formData.licenses,

    // Step 3: Model (obligatoires)
    transactionTypes: formData.transactionTypes,
    ndaEnforcement: formData.ndaEnforcement,

    // Step 4: Methodology (obligatoires)
    postSaleRole: formData.postSaleRole,
    vettingProcess: formData.vettingProcess,
    acceptedPofTypes: formData.acceptedPofTypes,
    competingOffers: formData.competingOffers,
    dealSizeMin: formData.dealSizeMin,
    dealSizeMax: formData.dealSizeMax,
    feeStructure: formData.feeStructure,

    // Step 5: Compliance (obligatoires)
    termsAccepted: formData.termsAccepted,
    termsVersion: '1.0.0',
  };

  // Propriétés optionnelles - incluses seulement si elles ont une valeur
  if (formData.firmName) {
    dto.firmName = formData.firmName;
  }
  if (formData.spokenLanguages && formData.spokenLanguages.length > 0) {
    dto.spokenLanguages = formData.spokenLanguages;
  }
  if (formData.cashDownMinPercent) {
    dto.cashDownMinPercent = formData.cashDownMinPercent;
  }
  if (formData.fixedAmountMin) {
    dto.fixedAmountMin = formData.fixedAmountMin;
  }
  if (formData.fixedAmountMax) {
    dto.fixedAmountMax = formData.fixedAmountMax;
  }

  return dto;
}

/**
 * Submit broker registration form
 * Uses apiClient which automatically includes Firebase Auth token
 */
export async function submitBrokerRegistration(
  formData: BrokerFormData
): Promise<BrokerRegistrationResponse> {
  try {
    const dto = transformFormDataToDto(formData);
    const response = await apiClient.post<BrokerRegistrationResponse>(
      '/broker/registration',
      dto
    );
    return response.data;
  } catch (error) {
    console.error('Broker registration error:', error);
    throw new Error(handleApiError(error));
  }
}

/**
 * Save draft of broker registration
 * Uses apiClient which automatically includes Firebase Auth token
 */
export async function saveBrokerRegistrationDraft(
  formData: BrokerFormData,
  step: number
): Promise<{ success: boolean; draftId: string }> {
  try {
    const response = await apiClient.post<{ success: boolean; draftId: string }>(
      '/broker/registration/draft',
      {
        formData,
        step,
        timestamp: new Date().toISOString(),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Draft save error:', error);
    // Fallback to localStorage if backend fails
    return {
      success: true,
      draftId: `local-${Date.now()}`,
    };
  }
}

/**
 * Local storage helper for saving form progress
 */
export function saveFormToLocalStorage(formData: BrokerFormData, step: number): void {
  try {
    const dataToSave = {
      formData,
      step,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('brokerRegistrationDraft', JSON.stringify(dataToSave));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
}

/**
 * Local storage helper for loading form progress
 */
export function loadFormFromLocalStorage(): {
  formData: BrokerFormData | null;
  step: number;
} | null {
  try {
    const savedData = localStorage.getItem('brokerRegistrationDraft');
    if (!savedData) return null;

    const parsed = JSON.parse(savedData);
    // Check if data is less than 7 days old
    const timestamp = new Date(parsed.timestamp);
    const now = new Date();
    const daysDiff = (now.getTime() - timestamp.getTime()) / (1000 * 60 * 60 * 24);

    if (daysDiff > 7) {
      // Data is too old, clear it
      localStorage.removeItem('brokerRegistrationDraft');
      return null;
    }

    return {
      formData: parsed.formData,
      step: parsed.step,
    };
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return null;
  }
}

/**
 * Clear saved draft from local storage
 */
export function clearFormFromLocalStorage(): void {
  try {
    localStorage.removeItem('brokerRegistrationDraft');
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
}
